import { http } from './request';
import type {
  InfluencerInsight,
  CreateInsightRequest,
  CreateBatchInsightsRequest,
  Campaign,
  CreateCampaignRequest,
  UpdateCampaignRequest,
  OutreachMessage,
  GenerateMessageRequest,
  UpdateMessageRequest,
  SendTask,
  SendProgress,
  BatchSendRequest,
  SendStats,
  Reply,
  ReplyStats,
  ReplyAnalysis,
  AnalyzeReplyResponse,
  GenerateFollowUpRequest,
  FollowUpContent,
  GenerateFollowUpResponse,
  UpdateFollowUpRequest,
  EmailAccount,
  CreateEmailAccountRequest,
  UpdateEmailAccountRequest,
} from '../types/outreach';
import type { ListData, PaginationParams } from '../types/api';

export const outreachApi = {
  // 红人洞察
  createInsight: (data: CreateInsightRequest) =>
    http.post<InfluencerInsight>('/api/v1/outreach/insights', data),

  createBatchInsights: (data: CreateBatchInsightsRequest) =>
    http.post<InfluencerInsight[]>('/api/v1/outreach/insights/batch', data),

  getInsight: (insightId: string) =>
    http.get<InfluencerInsight>(`/api/v1/outreach/insights/${insightId}`),

  // 邀约活动
  getCampaigns: (params?: PaginationParams) =>
    http.get<ListData<Campaign>>('/api/v1/outreach/campaigns', params),

  createCampaign: (data: CreateCampaignRequest) =>
    http.post<Campaign>('/api/v1/outreach/campaigns', data),

  getCampaign: (campaignId: string) =>
    http.get<Campaign>(`/api/v1/outreach/campaigns/${campaignId}`),

  updateCampaign: (campaignId: string, data: UpdateCampaignRequest) =>
    http.put<Campaign>(`/api/v1/outreach/campaigns/${campaignId}`, data),

  deleteCampaign: (campaignId: string) =>
    http.delete<void>(`/api/v1/outreach/campaigns/${campaignId}`),

  // 邀约消息
  generateMessages: (campaignId: string, data: GenerateMessageRequest) =>
    http.post<OutreachMessage[]>(`/api/v1/outreach/campaigns/${campaignId}/generate-messages`, data),

  getMessages: (campaignId: string, params?: PaginationParams) =>
    http.get<ListData<OutreachMessage>>(`/api/v1/outreach/campaigns/${campaignId}/messages`, params),

  updateMessage: (campaignId: string, messageId: string, data: UpdateMessageRequest) =>
    http.put<OutreachMessage>(`/api/v1/outreach/campaigns/${campaignId}/messages/${messageId}`, data),

  // 注意：后端路由使用 :msgId 而非 :messageId
  updateMessageById: (campaignId: string, msgId: string, data: UpdateMessageRequest) =>
    http.put<OutreachMessage>(`/api/v1/outreach/campaigns/${campaignId}/messages/${msgId}`, data),

  // 批量发送（后端不需要请求体，自动发送该活动下所有ready状态的消息）
  sendMessages: (campaignId: string) =>
    http.post<{ message: string }>(`/api/v1/outreach/campaigns/${campaignId}/send`),

  // 批量发送（支持定时发送）
  batchSend: (campaignId: string, data: BatchSendRequest) =>
    http.post<{ message: string; count: number; scheduled_at?: string }>(`/api/v1/outreach/campaigns/${campaignId}/batch-send`, data),

  getSendStats: (campaignId: string) =>
    http.get<SendStats>(`/api/v1/outreach/campaigns/${campaignId}/stats`),

  // 发送进度
  getSendProgress: (campaignId: string) =>
    http.get<SendProgress>(`/api/v1/outreach/campaigns/${campaignId}/send-progress`),

  // 发送任务列表
  listSendTasks: (campaignId: string, params?: PaginationParams & { status?: string }) =>
    http.get<ListData<SendTask>>(`/api/v1/outreach/campaigns/${campaignId}/send-tasks`, params),

  // 手动重试发送任务
  retrySendTask: (taskId: string) =>
    http.post<{ message: string }>(`/api/v1/outreach/send-tasks/${taskId}/retry`),

  // 回复监测 - 新接口
  // 获取活动的回复列表
  listReplies: (campaignId: string, params?: { sentiment?: string; is_read?: boolean; page?: number; page_size?: number }) =>
    http.get<ListData<Reply>>(`/api/v1/outreach/campaigns/${campaignId}/replies`, params),

  // 获取回复详情
  getReply: (replyId: string) =>
    http.get<Reply>(`/api/v1/outreach/replies/${replyId}`),

  // 触发意向分析
  analyzeReply: (replyId: string) =>
    http.post<AnalyzeReplyResponse>(`/api/v1/outreach/replies/${replyId}/analyze`),

  // 生成跟进内容
  generateFollowUp: (replyId: string) =>
    http.post<GenerateFollowUpResponse>(`/api/v1/outreach/replies/${replyId}/generate-followup`),

  // 标记回复为已读
  markReplyRead: (replyId: string) =>
    http.put<{ message: string }>(`/api/v1/outreach/replies/${replyId}/read`),

  // 更新跟进内容
  updateFollowUp: (replyId: string, data: UpdateFollowUpRequest) =>
    http.put<{ message: string }>(`/api/v1/outreach/replies/${replyId}/follow-up`, data),

  // 获取回复统计
  getReplyStats: (campaignId: string) =>
    http.get<ReplyStats>(`/api/v1/outreach/campaigns/${campaignId}/reply-stats`),

  // 兼容旧接口
  getReplies: (params?: PaginationParams & { intent?: string }) =>
    http.get<ListData<Reply>>('/api/v1/outreach/replies', params),

  // 邮箱账号管理
  getEmailAccounts: () =>
    http.get<EmailAccount[]>('/api/v1/outreach/email-accounts'),

  createEmailAccount: (data: CreateEmailAccountRequest) =>
    http.post<EmailAccount>('/api/v1/outreach/email-accounts', data),

  updateEmailAccount: (accountId: string, data: UpdateEmailAccountRequest) =>
    http.put<EmailAccount>(`/api/v1/outreach/email-accounts/${accountId}`, data),

  deleteEmailAccount: (accountId: string) =>
    http.delete<void>(`/api/v1/outreach/email-accounts/${accountId}`),

  testEmailAccount: (accountId: string) =>
    http.post<{ success: boolean; message: string }>(`/api/v1/outreach/email-accounts/${accountId}/test`),
};
